import React, { useState, useRef, useEffect } from 'react';
import { IconButton, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, CircularProgress } from '@mui/material';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { logoutAgentToolbar } from '../../utils/util';
import './AgentToolbarIframe.css';
import { Remove } from '@mui/icons-material';
import StyledTooltip from '../admin/common/StylesTooltip';
import { createPortal } from 'react-dom';

interface AgentToolbarIframeProps {
    url: string;
    onClose: () => void;
}

interface Position {
    x: number;
    y: number;
}

interface Size {
    width: number;
    height: number;
}

const AgentToolbarIframe: React.FC<AgentToolbarIframeProps> = ({ url, onClose }) => {
    const [isMinimized, setIsMinimized] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragPosition, setDragPosition] = useState<Position | null>(null);
    const [autoSize, setAutoSize] = useState<Size | null>(null);
    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
    const dragRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const dragStartPos = useRef<Position>({ x: 0, y: 0 });
    const elementStartPos = useRef<Position>({ x: 0, y: 0 });
    const [loading, setLoading] = useState(false);

    function resetIframe() {
        setDragPosition({ x: 120, y: window.innerHeight - 40 });
        setAutoSize(null);
        setIsMinimized(false);
    }

    // Reset position when iframe is first opened
    useEffect(() => {
        if (!dragPosition) {
            resetIframe();
        }
    }, [dragPosition]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            try {
                if (!isDragging || !isMinimized) return;

                const deltaX = e.clientX - dragStartPos.current.x;
                const deltaY = e.clientY - dragStartPos.current.y;

                const newPosition = {
                    x: elementStartPos.current.x + deltaX,
                    y: elementStartPos.current.y + deltaY
                };

                // Keep within viewport bounds
                const viewport = {
                    width: window.innerWidth,
                    height: window.innerHeight
                };

                newPosition.x = Math.max(0, Math.min(newPosition.x, viewport.width - 320));
                newPosition.y = Math.max(0, Math.min(newPosition.y, viewport.height - 60));

                setDragPosition(newPosition);
            } catch (error) {
                console.log("error in handleMouseMove", error)
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            try {
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
            } catch (error) {
                console.log("error in handleMouseUp", error)
            }
        }

        return () => {
            try {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            } catch (error) {
                console.log("error in useEffect mouse event return for agent toolbar iframe", error)
            }
        };
    }, [isDragging, isMinimized]);

    const calculateOptimalSize = (position: Position): Size => {
        try {
            const viewport = {
                width: window.innerWidth,
                height: window.innerHeight
            };

            const minWidth = 360;
            const minHeight = 460;
            const maxWidth = 380;
            const maxHeight = 520;

            // Calculate available space from the drag position
            const availableWidth = Math.min(viewport.width - position.x - 40, maxWidth);
            const availableHeight = Math.min(viewport.height - position.y - 40, maxHeight);

            // Ensure minimum sizes
            const finalWidth = Math.max(availableWidth, minWidth);
            const finalHeight = Math.max(availableHeight, minHeight);

            // If the calculated size would go off-screen, position it differently
            if (position.x + finalWidth > viewport.width) {
                // Position it to the left of the drag point instead
                const leftSpace = position.x - 40;
                return {
                    width: Math.max(Math.min(leftSpace, maxWidth), minWidth),
                    height: finalHeight
                };
            }

            return {
                width: finalWidth,
                height: finalHeight
            };
        } catch (error) {
            console.log("error in calculateOptimalSize", error)
            return {
                width: 360,
                height: 460
            };
        }
    };

    const handleMinimize = () => {
        try {
            if (!isMinimized && dragPosition) {
                // When maximizing from a dragged position, calculate optimal size
                const optimalSize = calculateOptimalSize(dragPosition);
                setAutoSize(optimalSize);
            }
            setIsMinimized((prev) => !prev);
        } catch (error) {
            console.log("error in handleMinimize", error)
        }
    };

    const handleCloseClick = () => {
        setShowCloseConfirmation(true);
    };

    const handleCloseCancel = () => {
        setShowCloseConfirmation(false);
    };

    const handleCloseContinue = async () => {
        try {
            setShowCloseConfirmation(false);
            setLoading(true);
            logoutAgentToolbar();
            const isAgentToolbarOpen = document.getElementById('OzonetelAgentToolbar') !== null;
            if (isAgentToolbarOpen) {
                const waitForAgentLogout = () => {
                    return new Promise<void>((resolve) => {
                        const handleMessage = (event: MessageEvent) => {
                            if (event.data && event.data.action === 'logout') {
                                window.removeEventListener('message', handleMessage);
                                resolve();
                            }
                        };
                        window.addEventListener('message', handleMessage);
                        setTimeout(() => {
                            window.removeEventListener('message', handleMessage);
                            resolve(); // Proceed with admin logout even if no response if it takes more than 10 seconds
                        }, 10000);
                    });
                };
                logoutAgentToolbar();
                await waitForAgentLogout();
            }
        } catch (error) {
            console.error("Error during close process:", error);
        } finally {
            setShowCloseConfirmation(false);
            onClose();
            setLoading(false);
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        try {
            if (!isMinimized) return; // Only draggable when minimized

            // Don't drag if clicking on control buttons
            if ((e.target as HTMLElement).closest('.iframe-controls')) {
                return;
            }

            setIsDragging(true);
            dragStartPos.current = { x: e.clientX, y: e.clientY };

            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                elementStartPos.current = { x: rect.left, y: rect.top };
            }

            e.preventDefault();
        } catch (error) {
            console.log("error in handleMouseDown", error)
        }
    };

    const getContainerStyle = () => {
        try {
            if (dragPosition && isMinimized) {
                return {
                    position: 'fixed' as const,
                    left: `${dragPosition.x}px`,
                    top: `${dragPosition.y}px`,
                    bottom: 'auto',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    zIndex: 2147483647
                };
            }

            if (dragPosition && !isMinimized && autoSize) {
                // Calculate position for maximized view
                const adjustedPosition = { ...dragPosition };

                // If expanding would go off-screen to the right, move it left
                if (dragPosition.x + autoSize.width > window.innerWidth) {
                    adjustedPosition.x = Math.max(0, window.innerWidth - autoSize.width - 20);
                }

                // If expanding would go off-screen to the bottom, move it up
                if (dragPosition.y + autoSize.height > window.innerHeight) {
                    adjustedPosition.y = Math.max(0, window.innerHeight - autoSize.height - 20);
                }

                return {
                    position: 'fixed' as const,
                    left: `${adjustedPosition.x}px`,
                    top: `${adjustedPosition.y}px`,
                    bottom: 'auto',
                    width: `${autoSize.width}px`,
                    height: `${autoSize.height}px`,
                    maxHeight: 'none',
                    zIndex: 2147483647
                };
            }
            return {};
        } catch (error) {
            console.log("error in getContainerStyle", error)
            return {};
        }
    };

    return createPortal(
        <Paper
            ref={containerRef}
            className={`agent-toolbar-iframe-container ${isMinimized ? 'minimized' : ''} ${isDragging ? 'dragging' : ''}`}
            elevation={3}
            style={getContainerStyle()}
        >
            <div
                ref={dragRef}
                className="iframe-header"
                onMouseDown={handleMouseDown}
                style={{ cursor: isMinimized ? (isDragging ? 'grabbing' : 'grab') : 'default', zIndex: 2147483647 }}
            >
                <div className="iframe-title">Agent Toolbar</div>
                <div className="iframe-controls">
                    {loading ? <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography fontSize={12} color="text.secondary">Logging out...</Typography>
                        <CircularProgress size={16} />
                    </div> : null}
                    <StyledTooltip title={isMinimized ? "Expand" : "Minimize"}>
                        <IconButton
                            className='iframe-controls-button'
                            size="small"
                            onClick={handleMinimize}
                        >
                            {isMinimized ? <CropFreeIcon fontSize="small" /> : <Remove sx={{}} fontSize="small" />}
                        </IconButton>
                    </StyledTooltip>
                </div>
            </div>
            <div className="iframe-content">
                <iframe
                    sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-modals allow-downloads"
                    allow="geolocation; microphone; camera;display-capture"
                    name="OzonetelAgentToolbar" id="OzonetelAgentToolbar" title="OzonetelAgentToolbar"
                    src={url}
                    width="100%"
                    height="100%"
                    style={{ border: '0' }}
                />
            </div>

            {/* Confirmation Dialog */}
            <Dialog
                open={showCloseConfirmation}
                onClose={handleCloseCancel}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '10px'
                    }
                }}
            >
                <DialogTitle>
                    Confirm Logout
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Closing the window will logout Agent Toolbar if agent is logged in. Are you sure you want to continue?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCancel} color="primary" variant="outlined" sx={{ borderRadius: '8px' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleCloseContinue} color="primary" variant="contained" sx={{ borderRadius: '8px' }}>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>,
        document.body
    );
};

export default AgentToolbarIframe; 